import { useState, useEffect } from 'react';
import { Check, Clock, User, MapPin, ChevronRight, Star, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { services, specialists, branches, addOns, generateTimeSlots, stats } from '../data/mockData';
import { toast } from 'sonner';

const STEPS = ['Servicio', 'Especialista', 'Sucursal', 'Hora'];

export const BookingConsole = ({ onComplete, preselectedService }) => {
  const [activeTab, setActiveTab] = useState('agendar');
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  
  // Booking state
  const [selectedService, setSelectedService] = useState(preselectedService || null);
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [selectedSpecialist, setSelectedSpecialist] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [timeSlots, setTimeSlots] = useState([]);

  // Initialize with preselected service
  useEffect(() => {
    if (preselectedService) {
      setSelectedService(preselectedService);
      setCurrentStep(1);
    }
  }, [preselectedService]);

  // Load time slots when on step 3 (time selection) and branch is selected
  useEffect(() => {
    if (selectedBranch && currentStep === 3) {
      setIsLoading(true);
      setTimeSlots([]);
      // Simulate API call
      const timer = setTimeout(() => {
        const slots = generateTimeSlots(selectedBranch.id, new Date());
        setTimeSlots(slots);
        setIsLoading(false);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [selectedBranch, currentStep]);

  const handleServiceSelect = (service) => {
    setSelectedService(service);
    setSelectedAddOns([]);
    setCurrentStep(1);
  };

  const handleAddOnToggle = (addOn) => {
    setSelectedAddOns((prev) =>
      prev.find((a) => a.id === addOn.id)
        ? prev.filter((a) => a.id !== addOn.id)
        : [...prev, addOn]
    );
  };

  const handleSpecialistSelect = (specialist) => {
    setSelectedSpecialist(specialist);
    // Auto-select first available branch for specialist
    const availableBranch = branches.find(b => specialist.availability.includes(b.id));
    setSelectedBranch(availableBranch || null);
    setCurrentStep(2);
  };

  const handleBranchSelect = (branch) => {
    setSelectedBranch(branch);
    setSelectedTime(null);
    setCurrentStep(3);
  };

  const handleTimeSelect = (slot) => {
    if (slot.status === 'full') return;
    setSelectedTime(slot);
  };

  const handleConfirm = () => {
    if (!selectedService || !selectedSpecialist || !selectedBranch || !selectedTime) {
      toast.error('Por favor completa todos los pasos');
      return;
    }
    
    onComplete?.({
      service: selectedService,
      addOns: selectedAddOns,
      specialist: selectedSpecialist,
      branch: selectedBranch,
      time: selectedTime
    });
  };

  const goBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const getTotalPrice = () => {
    const servicePrice = selectedService?.price || 0;
    const addOnsPrice = selectedAddOns.reduce((sum, a) => sum + a.price, 0);
    return servicePrice + addOnsPrice;
  };

  const getTotalDuration = () => {
    const serviceDuration = selectedService?.duration || 0;
    const addOnsDuration = selectedAddOns.reduce((sum, a) => sum + a.duration, 0);
    return serviceDuration + addOnsDuration;
  };

  const filteredSpecialists = selectedService
    ? specialists.filter((s) =>
        s.specialties.some((sp) =>
          selectedService.category.toLowerCase().includes(sp.toLowerCase()) ||
          sp.toLowerCase().includes(selectedService.category.toLowerCase().slice(0, -1))
        )
      )
    : specialists;

  const filteredBranches = selectedSpecialist
    ? branches.filter((b) => selectedSpecialist.availability.includes(b.id))
    : branches;

  // Skeleton component
  const Skeleton = ({ className }) => (
    <div className={`skeleton-loading rounded-lg ${className}`} />
  );

  return (
    <div className="booking-console booking-console-inner p-4 md:p-8" data-testid="booking-console">
      {/* Trust signals */}
      <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4 md:gap-6 mb-6 pb-6 border-b border-border/50">
        <div className="flex items-center gap-2 text-sm">
          <Star className="w-4 h-4 text-copper fill-copper" />
          <span className="font-medium">{stats.averageRating}</span>
          <span className="text-muted-foreground">({stats.totalReviews} reseñas)</span>
        </div>
        <div className="text-sm text-muted-foreground">
          +{stats.monthlyBookings.toLocaleString()} reservas/mes
        </div>
        <div className="text-sm text-muted-foreground hidden sm:block">
          Cancelación fácil
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-6 bg-soft-gray/50">
          <TabsTrigger
            value="agendar"
            data-testid="tab-agendar"
            className="data-[state=active]:bg-graphite data-[state=active]:text-cream"
          >
            Agendar
          </TabsTrigger>
          <TabsTrigger
            value="reagendar"
            data-testid="tab-reagendar"
            className="data-[state=active]:bg-graphite data-[state=active]:text-cream"
          >
            Reagendar
          </TabsTrigger>
          <TabsTrigger
            value="walkin"
            data-testid="tab-walkin"
            className="data-[state=active]:bg-graphite data-[state=active]:text-cream"
          >
            Walk-in
          </TabsTrigger>
        </TabsList>

        <TabsContent value="agendar" className="mt-0">
          {/* Step indicator */}
          <div className="flex flex-wrap items-center justify-center gap-2 md:gap-4 mb-8">
            {STEPS.map((step, index) => (
              <div key={step} className="flex items-center">
                <div
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    index === currentStep
                      ? 'bg-copper text-cream'
                      : index < currentStep
                      ? 'bg-muted-green/20 text-muted-green'
                      : 'bg-soft-gray text-muted-foreground'
                  }`}
                >
                  {index < currentStep ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <span className="w-4 h-4 flex items-center justify-center text-xs">
                      {index + 1}
                    </span>
                  )}
                  <span className="hidden sm:inline">{step}</span>
                </div>
                {index < STEPS.length - 1 && (
                  <ChevronRight className="hidden sm:block w-4 h-4 mx-1 text-muted-foreground" />
                )}
              </div>
            ))}
          </div>

          {/* Step content */}
          <div className="min-h-[300px]">
            {/* Step 1: Service */}
            {currentStep === 0 && (
              <div className="animate-fade-in space-y-3" data-testid="step-service">
                <h3 className="font-heading text-xl mb-4">Elige tu servicio</h3>
                <div className="grid gap-3 max-h-[280px] sm:max-h-[350px] overflow-y-auto pr-2">
                  {services.slice(0, 8).map((service) => (
                    <button
                      key={service.id}
                      onClick={() => handleServiceSelect(service)}
                      data-testid={`service-${service.id}`}
                      className={`service-card w-full p-4 rounded-xl border text-left ${
                        selectedService?.id === service.id
                          ? 'border-copper bg-copper/5'
                          : 'border-border/50 hover:border-copper/50 bg-white'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{service.name}</span>
                            {service.popular && (
                              <Badge className="badge-limited text-xs">Popular</Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-1">
                            {service.description}
                          </p>
                        </div>
                        <div className="text-right ml-4">
                          <p className="font-semibold text-copper">
                            ${service.price}
                          </p>
                          <p className="text-xs text-muted-foreground flex items-center justify-end gap-1">
                            <Clock className="w-3 h-3" />
                            {service.duration} min
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Specialist */}
            {currentStep === 1 && (
              <div className="animate-fade-in space-y-4" data-testid="step-specialist">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading text-xl">Elige especialista</h3>
                  <button onClick={goBack} className="text-sm text-copper hover:underline">
                    Cambiar servicio
                  </button>
                </div>

                {/* Add-ons for selected service */}
                {selectedService?.addOns.length > 0 && (
                  <div className="mb-6 p-4 bg-soft-gray/30 rounded-xl">
                    <p className="text-sm font-medium mb-3">Agregar extras:</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedService.addOns.map((addOnId) => {
                        const addOn = addOns.find((a) => a.id === addOnId);
                        if (!addOn) return null;
                        const isSelected = selectedAddOns.find((a) => a.id === addOn.id);
                        return (
                          <button
                            key={addOn.id}
                            onClick={() => handleAddOnToggle(addOn)}
                            data-testid={`addon-${addOn.id}`}
                            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                              isSelected
                                ? 'border-copper bg-copper/10 text-copper'
                                : 'border-border hover:border-copper/50'
                            }`}
                          >
                            {addOn.name} +${addOn.price}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 max-h-[260px] sm:max-h-[300px] overflow-y-auto pr-2">
                  {filteredSpecialists.map((specialist) => (
                    <button
                      key={specialist.id}
                      onClick={() => handleSpecialistSelect(specialist)}
                      data-testid={`specialist-${specialist.id}`}
                      className={`group relative overflow-hidden rounded-xl aspect-[3/4] text-left ${
                        selectedSpecialist?.id === specialist.id
                          ? 'ring-2 ring-copper'
                          : ''
                      }`}
                    >
                      <img
                        src={specialist.image}
                        alt={specialist.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="team-card-overlay absolute inset-0 flex flex-col justify-end p-3">
                        <p className="text-white font-medium text-sm">{specialist.name}</p>
                        <p className="text-white/70 text-xs">{specialist.role}</p>
                        <div className="flex items-center gap-1 mt-1">
                          <Star className="w-3 h-3 text-copper fill-copper" />
                          <span className="text-white/90 text-xs">{specialist.rating}</span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 3: Branch */}
            {currentStep === 2 && (
              <div className="animate-fade-in space-y-4" data-testid="step-branch">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading text-xl">Elige sucursal</h3>
                  <button onClick={goBack} className="text-sm text-copper hover:underline">
                    Cambiar especialista
                  </button>
                </div>

                <div className="flex flex-wrap gap-3 mb-6">
                  {filteredBranches.map((branch) => (
                    <button
                      key={branch.id}
                      onClick={() => handleBranchSelect(branch)}
                      data-testid={`branch-${branch.id}`}
                      className={`px-4 py-2 rounded-full border font-medium transition-colors ${
                        selectedBranch?.id === branch.id
                          ? 'border-copper bg-copper text-cream'
                          : 'border-border hover:border-copper'
                      }`}
                    >
                      {branch.name}
                    </button>
                  ))}
                </div>

                {selectedBranch && (
                  <div className="p-4 bg-soft-gray/30 rounded-xl">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-copper mt-0.5" />
                      <div>
                        <p className="font-medium">{selectedBranch.name}</p>
                        <p className="text-sm text-muted-foreground">{selectedBranch.address}</p>
                        <p className="text-sm text-muted-foreground mt-1">{selectedBranch.hours}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Time */}
            {currentStep === 3 && (
              <div className="animate-fade-in space-y-4" data-testid="step-time">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading text-xl">Elige horario</h3>
                  <button onClick={goBack} className="text-sm text-copper hover:underline">
                    Cambiar sucursal
                  </button>
                </div>

                {isLoading ? (
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                      {Array(12).fill(0).map((_, i) => (
                        <Skeleton key={i} className="h-10" />
                      ))}
                    </div>
                  ) : (
                  <>
                    <div className="flex gap-4 mb-4 text-xs">
                      <div className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded bg-soft-gray" />
                        <span className="text-muted-foreground">Lleno</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded bg-copper/20 border border-copper" />
                        <span className="text-muted-foreground">Casi lleno</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded border border-border" />
                        <span className="text-muted-foreground">Disponible</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 max-h-[220px] sm:max-h-[250px] overflow-y-auto pr-2">
                      {timeSlots.map((slot, index) => (
                        <button
                          key={index}
                          onClick={() => handleTimeSelect(slot)}
                          disabled={slot.status === 'full'}
                          data-testid={`time-${slot.time}`}
                          className={`time-slot px-3 py-2 rounded-lg border text-sm font-medium relative ${
                            selectedTime?.time === slot.time
                              ? 'border-copper bg-copper text-cream'
                              : slot.status === 'full'
                              ? 'full'
                              : slot.status === 'limited'
                              ? 'limited'
                              : 'border-border hover:border-copper available'
                          }`}
                        >
                          {slot.time}
                          {slot.isPeakHour && slot.status !== 'full' && (
                            <span className="absolute -top-1 -right-1 w-2 h-2 bg-muted-red rounded-full" />
                          )}
                          {slot.status === 'limited' && slot.remaining && (
                            <span className="block text-[10px] text-copper-dark">
                              Quedan {slot.remaining}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Summary & CTA */}
          {(selectedService || selectedTime) && (
            <div className="mt-6 pt-6 border-t border-border/50">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                  {selectedService && (
                    <p className="font-medium">
                      {selectedService.name}
                      {selectedAddOns.length > 0 && (
                        <span className="text-muted-foreground"> + {selectedAddOns.length} extras</span>
                      )}
                    </p>
                  )}
                  <p className="text-sm text-muted-foreground">
                    ${getTotalPrice()} • {getTotalDuration()} min
                    {selectedSpecialist && ` • ${selectedSpecialist.name}`}
                  </p>
                </div>
                <Button
                  onClick={handleConfirm}
                  disabled={!selectedTime}
                  data-testid="confirm-booking-btn"
                  className="w-full sm:w-auto bg-copper hover:bg-copper-dark text-cream px-8 py-3 rounded-full font-medium btn-scale"
                >
                  Confirmar cita
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="reagendar" className="mt-0">
          <div className="text-center py-12" data-testid="reagendar-content">
            <AlertCircle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-heading text-xl mb-2">Reagendar cita</h3>
            <p className="text-muted-foreground mb-6">
              Ingresa tu código de confirmación para reagendar
            </p>
            <input
              type="text"
              placeholder="Ej: CONF-12345"
              className="w-full max-w-xs mx-auto px-4 py-3 rounded-xl border border-border text-center"
            />
            <Button className="mt-4 bg-graphite hover:bg-graphite/90 text-cream px-8 py-3 rounded-full">
              Buscar cita
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="walkin" className="mt-0">
          <div className="text-center py-12" data-testid="walkin-content">
            <User className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-heading text-xl mb-2">Lista de espera Walk-in</h3>
            <p className="text-muted-foreground mb-6">
              Agrega tu nombre a la lista y te avisamos cuando sea tu turno
            </p>
            <div className="max-w-xs mx-auto space-y-3">
              <input
                type="text"
                placeholder="Tu nombre"
                className="w-full px-4 py-3 rounded-xl border border-border"
              />
              <input
                type="tel"
                placeholder="WhatsApp"
                className="w-full px-4 py-3 rounded-xl border border-border"
              />
              <Button 
                className="w-full bg-graphite hover:bg-graphite/90 text-cream py-3 rounded-full"
                onClick={() => toast.success('Te agregamos a la lista. Te avisamos pronto!')}
              >
                Unirse a la lista
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              Tiempo de espera estimado: 15-30 min
            </p>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BookingConsole;
